import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, ArrowRight, Clock } from 'lucide-react';
import { Button } from '@medikei/ui';
import { useMyAppointments, useAppointments } from '@/hooks/scheduling/useAppointments';
import type { AppointmentListItem } from '@/api';

interface ActiveConsultationBannerProps {
  role: 'PATIENT' | 'DOCTOR';
}

export const ActiveConsultationBanner: React.FC<ActiveConsultationBannerProps> = ({ role }) => {
  const navigate = useNavigate();
  const isDoctor = role === 'DOCTOR';

  // Busca agendamentos recentes/do dia respeitando a role do usuário (evita 404 de doctor profile para pacientes)
  const { data: doctorAppointmentsData } = useMyAppointments(
    { limit: 10, offset: 0 },
    { enabled: isDoctor }
  );

  const { data: patientAppointmentsData } = useAppointments(
    { limit: 10, offset: 0 },
    { enabled: !isDoctor }
  );

  const appointments: AppointmentListItem[] = isDoctor
    ? (doctorAppointmentsData?.appointments || [])
    : (patientAppointmentsData?.appointments || []);

  // Localiza consulta de telemedicina ativa ou na janela imediata de atendimento
  const activeAppointment = appointments.find((apt) => {
    const isTelemed =
      apt.serviceType === 'TELEMEDICINA' ||
      (apt as any).modality === 'TELEMEDICINA' ||
      (apt as any).type === 'TELEMEDICINE' ||
      Boolean(apt.joinUrl) ||
      apt.canJoin;

    if (!isTelemed) return false;

    // Se já estiver em andamento, é ativa imediatamente
    if (apt.status === 'IN_PROGRESS') return true;

    // Se estiver confirmada/paga/aguardando, verifica se está na janela (15 min antes até 60 min depois)
    const isConfirmed =
      apt.status === 'PAID' ||
      apt.status === 'DOCTOR_ACCEPTED' ||
      apt.status === 'WAITING_DOCTOR' ||
      (apt.status as string) === 'CONFIRMED';

    if (isConfirmed && apt.scheduledAt) {
      try {
        const aptTime = new Date(apt.scheduledAt).getTime();
        const now = Date.now();
        const diffMinutes = (aptTime - now) / (1000 * 60);
        return diffMinutes >= -60 && diffMinutes <= 15;
      } catch {
        return false;
      }
    }

    return false;
  });

  if (!activeAppointment) return null;

  const otherPersonName =
    role === 'PATIENT'
      ? activeAppointment.doctor?.name || 'Médico Plantonista'
      : activeAppointment.patient?.name || 'Paciente';

  const specialty =
    activeAppointment.doctor?.specialty ||
    (activeAppointment as any).specialty?.name ||
    'Telemedicina';

  const handleEnterCall = () => {
    if (role === 'PATIENT') {
      navigate(`/telemedicine/waiting/${activeAppointment.id}`);
    } else {
      navigate(`/medico/telemedicine/session/${activeAppointment.id}`);
    }
  };

  const formattedTime = activeAppointment.scheduledAt
    ? new Date(activeAppointment.scheduledAt).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  return (
    <div className="w-full mb-6 animate-in fade-in slide-in-from-top-3 duration-300">
      <div className="rounded-2xl border-2 border-emerald-500/40 bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent p-4 sm:p-5 shadow-sm backdrop-blur-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-start sm:items-center gap-3.5">
          <div className="relative shrink-0 mt-0.5 sm:mt-0">
            <div className="w-11 h-11 rounded-xl bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center border border-emerald-500/30">
              <Video className="w-5 h-5 animate-pulse" />
            </div>
            <span className="absolute -top-1 -right-1 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
            </span>
          </div>

          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800">
                {activeAppointment.status === 'IN_PROGRESS' ? 'Em Atendimento' : 'Pronta para Conectar'}
              </span>
              {formattedTime && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
                  <Clock className="w-3.5 h-3.5" />
                  Horário: {formattedTime}
                </span>
              )}
            </div>

            <h3 className="text-sm sm:text-base font-bold text-foreground flex items-center gap-1.5">
              <span>{role === 'PATIENT' ? 'Consulta com' : 'Paciente:'} {otherPersonName}</span>
              <span className="text-xs text-muted-foreground font-normal">({specialty})</span>
            </h3>

            <p className="text-xs text-muted-foreground">
              {role === 'PATIENT'
                ? 'Sua sala de teleconsulta já está disponível. Conecte seu microfone e câmera para iniciar.'
                : 'O horário do paciente está ativo. Inicie a chamada para abrir o prontuário e o vídeo.'}
            </p>
          </div>
        </div>

        <div className="shrink-0 flex items-center gap-2">
          <Button
            onClick={handleEnterCall}
            className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs sm:text-sm px-5 py-2.5 rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
          >
            <span>{role === 'PATIENT' ? 'Entrar na Teleconsulta' : 'Abrir Atendimento Clínico'}</span>
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
